import type { ComponentProps } from 'react'
import { FieldInfo, hasFieldError, useFieldContext, useFormReadOnly, useIsFieldOptional } from './shared'
import { Textarea } from '../../ui/textarea'
import { FieldLabelRow } from './field-label-row'
import { cn } from '@/lib/utils'

export default function TextareaField({
  label,
  className,
  optional,
  ...props
}: ComponentProps<'textarea'> & { label?: string; optional?: boolean }) {
  const field = useFieldContext<string>()
  const formReadOnly = useFormReadOnly()
  const isReadOnly = props.readOnly ?? formReadOnly
  const isOptional = useIsFieldOptional(field.name, optional)
  const value = field.state.value
  const displayValue = isReadOnly && !value ? '-' : value

  return (
    <div className={className}>
      <div className="grid gap-2">
        {label && (
          <FieldLabelRow
            label={label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
          />
        )}
        <Textarea
          id={field.name}
          name={field.name}
          aria-invalid={hasFieldError(field.state.meta)}
          value={displayValue}
          onBlur={field.handleBlur}
          onChange={(e) => field.handleChange(e.target.value)}
          {...props}
          readOnly={isReadOnly}
          tabIndex={isReadOnly ? -1 : props.tabIndex}
          className={cn(
            'min-h-24',
            isReadOnly &&
              'border-transparent bg-transparent px-0 focus-visible:border-transparent focus-visible:ring-0 dark:bg-transparent',
          )}
        />
      </div>
      <FieldInfo fieldState={field.state} />
    </div>
  )
}
